import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DemoEmailTester from "@/components/DemoEmailTester";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  ShoppingBag, 
  Mail, 
  BarChart3, 
  Settings, 
  Lock,
  Eye,
  FileText
} from "lucide-react";

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [reports, setReports] = useState<string[]>([]);

  const navigate = useNavigate();

  // Admin authentication (simple password check for demo purposes)
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Simple password check - in production would use proper authentication
    if (password === "admin123") {
      setIsAuthenticated(true);
      setError("");
      localStorage.setItem("jogngym_admin_auth", "true"); // Store auth state
    } else {
      setError("Invalid password");
    }
  };

  // Fetch real stats
  const fetchStats = async () => {
    const res = await fetch("/api/admin/stats");
    if (res.ok) setStats(await res.json());
  };

  // Fetch real users
  const fetchUsers = async () => {
    const res = await fetch("/api/admin/users");
    if (res.ok) setUsers(await res.json());
  };

  // Fetch PDF reports
  const fetchReports = async () => {
    const res = await fetch("/api/reports");
    if (res.ok) {
      const data = await res.json();
      setReports(data.reports || []);
    }
  };

  useEffect(() => {
    // Check for existing admin authentication
    const adminAuth = localStorage.getItem("jogngym_admin_auth");
    if (adminAuth === "true") {
      setIsAuthenticated(true);
      fetchStats();
      fetchUsers();
      fetchReports();
    }
  }, []);

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("jogngym_admin_auth");
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Card className="w-[350px]">
          <CardHeader>
            <CardTitle className="text-center">Admin Login</CardTitle>
            <CardDescription className="text-center">
              Enter your admin password to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Lock className="mr-2 h-4 w-4 text-slate-500" />
                    <Input
                      type="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <Button type="submit" className="w-full">Login</Button>
                <p className="text-xs text-center text-slate-500 mt-4">
                  This is a protected area. Unauthorized access is prohibited.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <Button variant="outline" onClick={handleLogout}>Logout</Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid grid-cols-4 sm:grid-cols-6 md:w-auto">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Products</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Reports</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Settings</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <h2 className="text-2xl font-bold">Dashboard Overview</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats ? stats.totalUsers || 0 : 'Loading...'}</div>
                <p className="text-xs text-slate-500">+2 since last week</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Newsletter Subscribers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats ? stats.subscribedUsers || 0 : 'Loading...'}</div>
                <p className="text-xs text-slate-500">+1 since last week</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Product Views</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats ? stats.productViews || 0 : 'Loading...'}</div>
                <p className="text-xs text-slate-500">+12 since yesterday</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Add to Cart Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats ? stats.addToCartEvents || 0 : 'Loading...'}</div>
                <p className="text-xs text-slate-500">+3 since yesterday</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <h2 className="text-2xl font-bold">User Management</h2>
          
          <div className="border rounded-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="py-3 px-4 text-left">User ID</th>
                  <th className="py-3 px-4 text-left">Email</th>
                  <th className="py-3 px-4 text-left">Subscribed</th>
                  <th className="py-3 px-4 text-left">Lead Score</th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b">
                    <td className="py-3 px-4">{user.id}</td>
                    <td className="py-3 px-4">{user.email}</td>
                    <td className="py-3 px-4">
                      {user.subscribed ? (
                        <span className="inline-block bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-xs">
                          Subscribed
                        </span>
                      ) : (
                        <span className="inline-block bg-slate-100 text-slate-800 px-2 py-1 rounded text-xs">
                          Not Subscribed
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">{user.leadScore}</td>
                    <td className="py-3 px-4">
                      <Button size="sm" variant="outline" className="h-8 w-8 p-0">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6">
          <h2 className="text-2xl font-bold">Product Management</h2>
          <p className="text-slate-500">Manage products, view analytics, and update inventory.</p>
          
          <div className="flex justify-end">
            <Button className="mb-4">Add New Product</Button>
          </div>
          
          <div className="border rounded-md p-4">
            <p className="text-center py-8">Product management interface would go here</p>
          </div>
        </TabsContent>

        {/* Email Testing Tab */}
        <TabsContent value="email" className="space-y-6">
          <h2 className="text-2xl font-bold">Email Testing</h2>
          <p className="text-slate-500 mb-4">
            Send test emails to verify email delivery functionality.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DemoEmailTester />
            
            <Card>
              <CardHeader>
                <CardTitle>Email Templates</CardTitle>
                <CardDescription>
                  Preview and test different email templates.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Button variant="outline" className="w-full justify-between">
                    Welcome Email
                    <Eye className="h-4 w-4 ml-2" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between">
                    Product Recommendations
                    <Eye className="h-4 w-4 ml-2" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between">
                    Abandoned Cart
                    <Eye className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <h2 className="text-2xl font-bold">Settings</h2>
          <p className="text-slate-500">Configure application settings.</p>
          
          <Card>
            <CardHeader>
              <CardTitle>Email Configuration</CardTitle>
              <CardDescription>
                Configure the email service settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">SMTP Server</label>
                    <Input type="text" defaultValue="smtp.gmail.com" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">SMTP Port</label>
                    <Input type="text" defaultValue="587" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">SMTP Username</label>
                    <Input type="text" defaultValue="your-email@gmail.com" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">SMTP Password</label>
                    <Input type="password" defaultValue="••••••••" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button>Save Settings</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <h2 className="text-2xl font-bold">Analytics PDF Reports</h2>
          <p className="text-slate-500">
            Download daily analytics reports generated by the backend.
          </p>
          
          <Card>
            <CardHeader>
              <CardTitle>Analytics PDF Reports</CardTitle>
              <CardDescription>
                Download daily analytics reports generated by the backend.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <p>No reports found.</p>
              ) : (
                <ul className="space-y-2">
                  {reports.map(filename => (
                    <li key={filename}>
                      <a
                        href={`/api/reports/${filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-700 underline"
                      >
                        {filename}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
